import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import {
  AlertTriangle,
  Download,
  Filter,
  ChevronDown,
  ChevronRight,
  Shield,
  FileText,
  Clock,
  XCircle,
  AlertCircle,
  CheckCircle2,
  Target,
  TrendingUp,
  User,
  ExternalLink,
} from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface GapItem {
  id: string
  control_id: string
  control_number: string
  control_name: string
  framework_id: string
  framework_name: string
  gap_type: 'no_evidence' | 'overdue_evidence' | 'missing_policy' | 'overdue_task' | 'non_compliant'
  gap_description: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  remediation_effort: 'high' | 'medium' | 'low'
  remediation_suggestion: string
  owner_id?: string
  owner_name?: string
  days_overdue?: number
  last_evidence_date?: number
  next_due_date?: number
}

interface GapAnalysisSummary {
  framework_id: string
  framework_name: string
  total_controls: number
  controls_with_gaps: number
  gap_rate: number
  critical_gaps: number
  high_gaps: number
  medium_gaps: number
  low_gaps: number
  gaps_by_type: {
    no_evidence: number
    overdue_evidence: number
    missing_policy: number
    overdue_task: number
    non_compliant: number
  }
}

interface CrossFrameworkSummary {
  generated_at: string
  generated_by: string
  totals: {
    total_controls: number
    controls_with_gaps: number
    gap_rate: number
    critical_gaps: number
    high_gaps: number
    medium_gaps: number
    low_gaps: number
  }
  frameworks: GapAnalysisSummary[]
}

interface Framework {
  id: string
  name: string
  display_name: string
}

// ============================================================================
// Component
// ============================================================================

export default function GapAnalysis() {
  const { toast, success, error: showError, warning } = useToast()

  // State
  const [loading, setLoading] = useState(true)
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [selectedFramework, setSelectedFramework] = useState<string>('')
  const [crossFrameworkSummary, setCrossFrameworkSummary] = useState<CrossFrameworkSummary | null>(null)
  const [frameworkReport, setFrameworkReport] = useState<{
    summary: GapAnalysisSummary
    gaps: GapItem[]
  } | null>(null)
  const [severityFilter, setSeverityFilter] = useState<string>('')
  const [gapTypeFilter, setGapTypeFilter] = useState<string>('')
  const [expandedGaps, setExpandedGaps] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)

  // Fetch frameworks and cross-framework summary on mount
  useEffect(() => {
    fetchInitialData()
  }, [])

  // Fetch framework-specific report when framework is selected
  useEffect(() => {
    if (selectedFramework) {
      fetchFrameworkReport()
    } else {
      setFrameworkReport(null)
    }
  }, [selectedFramework, severityFilter, gapTypeFilter])

  async function fetchInitialData() {
    setLoading(true)
    try {
      const [fwRes, summaryRes] = await Promise.all([
        api.getFrameworks(),
        api.getGapAnalysisSummary(),
      ])

      if (fwRes.success) {
        setFrameworks(fwRes.frameworks.filter((f: any) => f.enabled))
      }

      if (summaryRes.success) {
        setCrossFrameworkSummary(summaryRes.summary)
      }
    } catch (err) {
      console.error('Failed to load gap analysis data:', err)
      showError('Failed to load gap analysis data')
    } finally {
      setLoading(false)
    }
  }

  async function fetchFrameworkReport() {
    setLoading(true)
    try {
      const res = await api.getGapAnalysis(selectedFramework, {
        severity: severityFilter || undefined,
        gap_type: gapTypeFilter || undefined,
      })

      if (res.success && res.report) {
        setFrameworkReport({
          summary: res.report.summary,
          gaps: res.report.gaps,
        })
      }
    } catch (err) {
      console.error('Failed to load framework gap analysis:', err)
      showError('Failed to load framework gap analysis')
    } finally {
      setLoading(false)
    }
  }

  async function handleExport() {
    if (!selectedFramework) {
      warning('Please select a framework first')
      return
    }

    setExporting(true)
    try {
      const blob = await api.exportGapAnalysis(selectedFramework)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const framework = frameworks.find(f => f.id === selectedFramework)
      a.download = `gap-analysis-${framework?.display_name?.replace(/[^a-zA-Z0-9]/g, '-') || 'report'}-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      success('Gap analysis exported successfully')
    } catch (err) {
      console.error('Failed to export gap analysis:', err)
      showError('Failed to export gap analysis')
    } finally {
      setExporting(false)
    }
  }

  function toggleGapExpanded(gapId: string) {
    setExpandedGaps(prev => {
      const next = new Set(prev)
      if (next.has(gapId)) {
        next.delete(gapId)
      } else {
        next.add(gapId)
      }
      return next
    })
  }

  function getSeverityColor(severity: string) {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  function getEffortColor(effort: string) {
    switch (effort) {
      case 'high':
        return 'bg-purple-100 text-purple-800'
      case 'medium':
        return 'bg-indigo-100 text-indigo-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  function getGapTypeIcon(gapType: string) {
    switch (gapType) {
      case 'no_evidence':
        return <FileText className="h-4 w-4" />
      case 'overdue_evidence':
        return <Clock className="h-4 w-4" />
      case 'missing_policy':
        return <Shield className="h-4 w-4" />
      case 'overdue_task':
        return <AlertCircle className="h-4 w-4" />
      case 'non_compliant':
        return <XCircle className="h-4 w-4" />
      default:
        return <AlertTriangle className="h-4 w-4" />
    }
  }

  function formatGapType(gapType: string) {
    return gapType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  if (loading && !crossFrameworkSummary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gap Analysis</h1>
          <p className="text-gray-500">Identify compliance gaps and remediation priorities</p>
        </div>
        <button
          onClick={handleExport}
          disabled={!selectedFramework || exporting}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Cross-Framework Summary Cards */}
      {crossFrameworkSummary && !selectedFramework && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Controls</p>
                <p className="text-3xl font-bold text-gray-900">
                  {crossFrameworkSummary.totals.total_controls}
                </p>
              </div>
              <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                <Target className="h-6 w-6 text-primary-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Controls with Gaps</p>
                <p className="text-3xl font-bold text-orange-600">
                  {crossFrameworkSummary.totals.controls_with_gaps}
                </p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Gap Rate</p>
                <p className="text-3xl font-bold text-gray-900">
                  {crossFrameworkSummary.totals.gap_rate}%
                </p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Critical Gaps</p>
                <p className="text-3xl font-bold text-red-600">
                  {crossFrameworkSummary.totals.critical_gaps}
                </p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Framework Selector */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="label">Select Framework</label>
            <select
              value={selectedFramework}
              onChange={(e) => {
                setSelectedFramework(e.target.value)
                setSeverityFilter('')
                setGapTypeFilter('')
                setExpandedGaps(new Set())
              }}
              className="input max-w-md"
            >
              <option value="">All Frameworks (Summary View)</option>
              {frameworks.map((fw) => (
                <option key={fw.id} value={fw.id}>
                  {fw.display_name}
                </option>
              ))}
            </select>
          </div>

          {selectedFramework && (
            <div className="flex gap-4">
              <div>
                <label className="label">Severity</label>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="label">Gap Type</label>
                <select
                  value={gapTypeFilter}
                  onChange={(e) => setGapTypeFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Types</option>
                  <option value="non_compliant">Non-Compliant</option>
                  <option value="overdue_evidence">Overdue Evidence</option>
                  <option value="overdue_task">Overdue Task</option>
                  <option value="missing_policy">Missing Policy</option>
                  <option value="no_evidence">No Evidence</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cross-Framework Summary Table */}
      {!selectedFramework && crossFrameworkSummary && (
        <div className="card">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Gap Analysis by Framework</h2>
            <p className="text-sm text-gray-500">Click a framework to view detailed gaps</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Framework
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Controls
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gaps
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gap Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Critical
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    High
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medium
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Low
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {crossFrameworkSummary.frameworks.map((fw) => (
                  <tr
                    key={fw.framework_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedFramework(fw.framework_id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Shield className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{fw.framework_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {fw.total_controls}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">
                      {fw.controls_with_gaps}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-[80px]">
                          <div
                            className="h-2 bg-orange-500 rounded-full"
                            style={{ width: `${fw.gap_rate}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-700">{fw.gap_rate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {fw.critical_gaps > 0 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {fw.critical_gaps}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {fw.high_gaps > 0 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          {fw.high_gaps}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {fw.medium_gaps > 0 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {fw.medium_gaps}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {fw.low_gaps > 0 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {fw.low_gaps}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Framework-Specific Report */}
      {selectedFramework && frameworkReport && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="card p-4">
              <p className="text-sm font-medium text-gray-500">Total Controls</p>
              <p className="text-2xl font-bold text-gray-900">{frameworkReport.summary.total_controls}</p>
            </div>
            <div className="card p-4 border-l-4 border-red-500">
              <p className="text-sm font-medium text-gray-500">Critical</p>
              <p className="text-2xl font-bold text-red-600">{frameworkReport.summary.critical_gaps}</p>
            </div>
            <div className="card p-4 border-l-4 border-orange-500">
              <p className="text-sm font-medium text-gray-500">High</p>
              <p className="text-2xl font-bold text-orange-600">{frameworkReport.summary.high_gaps}</p>
            </div>
            <div className="card p-4 border-l-4 border-yellow-500">
              <p className="text-sm font-medium text-gray-500">Medium</p>
              <p className="text-2xl font-bold text-yellow-600">{frameworkReport.summary.medium_gaps}</p>
            </div>
            <div className="card p-4 border-l-4 border-blue-500">
              <p className="text-sm font-medium text-gray-500">Low</p>
              <p className="text-2xl font-bold text-blue-600">{frameworkReport.summary.low_gaps}</p>
            </div>
          </div>

          {/* Gap Type Breakdown */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gaps by Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <XCircle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-sm text-gray-500">Non-Compliant</p>
                  <p className="text-lg font-semibold">{frameworkReport.summary.gaps_by_type.non_compliant}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Clock className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-500">Overdue Evidence</p>
                  <p className="text-lg font-semibold">{frameworkReport.summary.gaps_by_type.overdue_evidence}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-500">Overdue Tasks</p>
                  <p className="text-lg font-semibold">{frameworkReport.summary.gaps_by_type.overdue_task}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Shield className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-500">Missing Policy</p>
                  <p className="text-lg font-semibold">{frameworkReport.summary.gaps_by_type.missing_policy}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">No Evidence</p>
                  <p className="text-lg font-semibold">{frameworkReport.summary.gaps_by_type.no_evidence}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Gap List */}
          <div className="card">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Gap Details ({frameworkReport.gaps.length} items)
              </h2>
              <p className="text-sm text-gray-500">
                Prioritized list of compliance gaps with remediation suggestions
              </p>
            </div>

            {frameworkReport.gaps.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Gaps Found</h3>
                <p className="text-gray-500">
                  {severityFilter || gapTypeFilter
                    ? 'No gaps match the selected filters. Try adjusting your filters.'
                    : 'Great job! All controls in this framework are properly documented.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {frameworkReport.gaps.map((gap) => (
                  <div key={gap.id} className="p-4 hover:bg-gray-50">
                    {/* Gap Header */}
                    <div
                      className="flex items-start gap-4 cursor-pointer"
                      onClick={() => toggleGapExpanded(gap.id)}
                    >
                      <button className="mt-1 text-gray-400">
                        {expandedGaps.has(gap.id) ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-sm font-mono font-medium text-primary-600">
                            {gap.control_number}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${getSeverityColor(gap.severity)}`}
                          >
                            {gap.severity.charAt(0).toUpperCase() + gap.severity.slice(1)}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                            {getGapTypeIcon(gap.gap_type)}
                            {formatGapType(gap.gap_type)}
                          </span>
                        </div>
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-1">
                          {gap.control_name}
                        </h4>
                        <p className="text-sm text-gray-500 line-clamp-1">
                          {gap.gap_description}
                        </p>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        {gap.owner_name && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <User className="h-4 w-4" />
                            {gap.owner_name}
                          </div>
                        )}
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEffortColor(gap.remediation_effort)}`}>
                          {gap.remediation_effort.charAt(0).toUpperCase() + gap.remediation_effort.slice(1)} Effort
                        </span>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedGaps.has(gap.id) && (
                      <div className="mt-4 ml-9 pl-4 border-l-2 border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Gap Details</h5>
                            <p className="text-sm text-gray-600 mb-3">{gap.gap_description}</p>

                            {gap.days_overdue && (
                              <p className="text-sm text-red-600 mb-2">
                                <Clock className="h-4 w-4 inline mr-1" />
                                {gap.days_overdue} days overdue
                              </p>
                            )}

                            {gap.last_evidence_date && (
                              <p className="text-sm text-gray-500">
                                Last evidence: {new Date(gap.last_evidence_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>

                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Remediation Suggestion</h5>
                            <p className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg border border-green-200">
                              {gap.remediation_suggestion}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4">
                          <Link
                            to={`/controls/${gap.control_id}`}
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                          >
                            View Control Details
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Loading State */}
      {loading && selectedFramework && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}
    </div>
  )
}
